import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  getEventsByAdherent,
  updateAdherentResults,
  unregisterAdherent,
} from "../../../../services/eventServices";
import { getAdherentsByUser } from "../../../../services/adherentServices";
import {
  addPalmares,
  checkPalmaresExists,
  updatePalmares,
} from "../../../../services/palmaresServices";
import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  DEFAULT_RESULT_FORM,
  DEFAULT_RESULTATS_FILTERS,
  countActiveResultFilters,
  dedupeResultEvents,
  extractPalmaresRecord,
  filterResultEvents,
  getAdherentEventIds,
  getPalmaresRecordId,
  getPalmaresStatusKey,
  getResultEventKey,
  getResultatsStats,
  normalizeAdherentsResponse,
  normalizeEventsResponse,
  sortResultEvents,
} from "../utils/resultatsUtils";

export function useResultatsPage() {
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);

  const [adherents, setAdherents] = useState([]);
  const [selectedAdherentId, setSelectedAdherentId] = useState(null);

  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_RESULTATS_FILTERS);
  const [form, setForm] = useState(DEFAULT_RESULT_FORM);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [adherentsLoading, setAdherentsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [palmaresStatus, setPalmaresStatus] = useState({});
  const [palmaresRecords, setPalmaresRecords] = useState({});
  const [deletingIds, setDeletingIds] = useState([]);
  const [addingPalmaresIds, setAddingPalmaresIds] = useState([]);

  const selectedAdherent = useMemo(() => {
    if (!adherents.length) return null;

    return (
      adherents.find(
        (adherent) => Number(adherent.id) === Number(selectedAdherentId),
      ) || adherents[0]
    );
  }, [adherents, selectedAdherentId]);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  const selectAdherent = useCallback((adherentId) => {
    setSelectedAdherentId(adherentId);
    setSelectedEvent(null);
    setIsEditModalOpen(false);
    setSelectedImage(null);
  }, []);

  const fetchAdherents = useCallback(async () => {
    setAdherentsLoading(true);

    try {
      const response = await getAdherentsByUser();
      const loadedAdherents = normalizeAdherentsResponse(response);

      setAdherents(loadedAdherents);

      if (loadedAdherents.length > 0) {
        setSelectedAdherentId((currentId) => {
          const stillExists = loadedAdherents.some(
            (adherent) => Number(adherent.id) === Number(currentId),
          );

          return stillExists ? currentId : loadedAdherents[0].id;
        });
      } else {
        setSelectedAdherentId(null);
        setEvents([]);
        setPalmaresStatus({});
        setPalmaresRecords({});

        notificationRef.current?.(
          "Aucun adhérent n'est rattaché à votre compte.",
          "error",
        );
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des adhérents :", error);

      notificationRef.current?.(
        "Erreur lors de la récupération des profils judokas.",
        "error",
      );
    } finally {
      setAdherentsLoading(false);
    }
  }, []);

  const fetchPalmaresStatus = useCallback(async (eventsToCheck = []) => {
    const status = {};
    const records = {};

    await Promise.all(
      eventsToCheck.map(async (event) => {
        const { adherentId, eventId } = getAdherentEventIds(event);
        const statusKey = getPalmaresStatusKey(event);

        if (!adherentId || !eventId) {
          status[statusKey] = false;
          records[statusKey] = null;
          return;
        }

        try {
          const response = await checkPalmaresExists(adherentId, eventId);
          const record = extractPalmaresRecord(response);

          status[statusKey] = Boolean(response?.exists || record);
          records[statusKey] = record;
        } catch (error) {
          console.error(
            "Erreur lors de la vérification du palmarès :",
            error,
            event,
          );

          status[statusKey] = false;
          records[statusKey] = null;
        }
      }),
    );

    return { status, records };
  }, []);

  const fetchData = useCallback(
    async ({ silent = false, adherentId = selectedAdherentId } = {}) => {
      if (!adherentId) {
        setEvents([]);
        setPalmaresStatus({});
        setPalmaresRecords({});
        setLoading(false);
        setRefreshing(false);
        return;
      }

      const isInitialLoad = !hasLoadedOnceRef.current;

      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const response = await getEventsByAdherent(adherentId);
        const safeEvents = normalizeEventsResponse(response);

        const uniqueEvents = dedupeResultEvents(safeEvents);
        const sortedEvents = sortResultEvents(uniqueEvents);

        setEvents(sortedEvents);

        const { status, records } = await fetchPalmaresStatus(sortedEvents);

        setPalmaresStatus(status);
        setPalmaresRecords(records);

        if (!silent && !isInitialLoad) {
          notificationRef.current?.("Résultats actualisés.", "success");
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des résultats :", error);

        notificationRef.current?.(
          "Erreur lors de la récupération des résultats.",
          "error",
        );
      } finally {
        hasLoadedOnceRef.current = true;
        setLoading(false);
        setRefreshing(false);
      }
    },
    [fetchPalmaresStatus, selectedAdherentId],
  );

  useEffect(() => {
    fetchAdherents();
  }, [fetchAdherents]);

  useEffect(() => {
    if (selectedAdherentId) {
      fetchData({ silent: true, adherentId: selectedAdherentId });
    }
  }, [fetchData, selectedAdherentId]);

  const filteredEvents = useMemo(() => {
    return filterResultEvents(events, filters, palmaresStatus);
  }, [events, filters, palmaresStatus]);

  const stats = useMemo(() => {
    return getResultatsStats(events, palmaresStatus);
  }, [events, palmaresStatus]);

  const activeFiltersCount = useMemo(() => {
    return countActiveResultFilters(filters);
  }, [filters]);

  const handleFilterChange = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_RESULTATS_FILTERS);
  }, []);

  const handleInputChange = useCallback((eventOrName, nextValue) => {
    if (typeof eventOrName === "string") {
      setForm((prev) => ({
        ...prev,
        [eventOrName]: nextValue,
      }));
      return;
    }

    const { name, value } = eventOrName.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const buildPalmaresPayload = useCallback((event, values = {}) => {
    const { adherentId, eventId } = getAdherentEventIds(event);
    const eventResult = {
      ...event?.AdherentEvent,
      ...values,
    };

    return {
      adherent_palmares_id: adherentId,
      event_palmares_id: eventId,
      date_palmares: event.start,
      titre_palmares: event.titre,
      resultats_palmares: eventResult?.resultats || "Non spécifié",
      classement_palmares: eventResult?.classement || "Non spécifié",
      observations_palmares: eventResult?.observations || "Non spécifié",
      podium_palmares: eventResult?.podium_url || "",
      tableau_palmares: eventResult?.tableau_url || "",
    };
  }, []);

  const handleEdit = useCallback((event) => {
    if (!event?.AdherentEvent) {
      notificationRef.current?.(
        "Impossible de modifier ce résultat : données manquantes.",
        "error",
      );
      return;
    }

    setSelectedEvent(event);
    setForm({
      resultats: event.AdherentEvent?.resultats || "",
      classement: event.AdherentEvent?.classement || "",
      observations: event.AdherentEvent?.observations || "",
      podium_url: event.AdherentEvent?.podium_url || "",
      tableau_url: event.AdherentEvent?.tableau_url || "",
    });
    setIsEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedEvent(null);
    setForm(DEFAULT_RESULT_FORM);
  }, []);

  const handleSubmit = useCallback(
    async (submitEvent) => {
      submitEvent.preventDefault();

      if (!selectedEvent?.AdherentEvent) {
        notificationRef.current?.(
          "Impossible d'enregistrer : événement invalide.",
          "error",
        );
        return;
      }

      const { adherentId, eventId } = getAdherentEventIds(selectedEvent);
      const statusKey = getPalmaresStatusKey(selectedEvent);

      if (!adherentId || !eventId) {
        notificationRef.current?.(
          "Données manquantes pour la mise à jour du résultat.",
          "error",
        );
        return;
      }

      setSaving(true);

      try {
        const payload = {
          adherent_id: adherentId,
          event_id: eventId,
          resultats: form.resultats,
          classement: form.classement,
          observations: form.observations,
          podium_url: form.podium_url,
          tableau_url: form.tableau_url,
        };

        await updateAdherentResults(payload);

        setEvents((prevEvents) =>
          prevEvents.map((event) => {
            const ids = getAdherentEventIds(event);

            if (
              Number(ids.adherentId) === Number(adherentId) &&
              Number(ids.eventId) === Number(eventId)
            ) {
              return {
                ...event,
                AdherentEvent: {
                  ...event.AdherentEvent,
                  ...payload,
                },
              };
            }

            return event;
          }),
        );

        /**
         * Synchronisation automatique du palmarès
         * si le résultat avait déjà été ajouté au palmarès.
         */
        if (palmaresStatus[statusKey]) {
          let palmaresRecord = palmaresRecords[statusKey];
          let palmaresId = getPalmaresRecordId(palmaresRecord);

          if (!palmaresId) {
            const checkResponse = await checkPalmaresExists(adherentId, eventId);
            palmaresRecord = extractPalmaresRecord(checkResponse);
            palmaresId = getPalmaresRecordId(palmaresRecord);
          }

          if (palmaresId) {
            const palmaresPayload = buildPalmaresPayload(selectedEvent, payload);
            const updatedPalmares = await updatePalmares(
              palmaresId,
              palmaresPayload,
            );

            setPalmaresRecords((prev) => ({
              ...prev,
              [statusKey]: extractPalmaresRecord(updatedPalmares) || {
                ...palmaresRecord,
                ...palmaresPayload,
                id: palmaresId,
              },
            }));

            notificationRef.current?.(
              "Résultat et palmarès mis à jour avec succès !",
              "success",
            );
          } else {
            notificationRef.current?.(
              "Résultat mis à jour, mais impossible de retrouver le palmarès à synchroniser.",
              "info",
            );
          }
        } else {
          notificationRef.current?.(
            "Résultat mis à jour avec succès !",
            "success",
          );
        }

        handleCloseEditModal();
      } catch (error) {
        console.error("Erreur lors de la mise à jour du résultat :", error);

        notificationRef.current?.(
          "Erreur lors de la mise à jour du résultat.",
          "error",
        );
      } finally {
        setSaving(false);
      }
    },
    [
      buildPalmaresPayload,
      form,
      handleCloseEditModal,
      palmaresRecords,
      palmaresStatus,
      selectedEvent,
    ],
  );

  const handleAddToPalmares = useCallback(
    async (event) => {
      const eventKey = getResultEventKey(event);
      const statusKey = getPalmaresStatusKey(event);

      if (addingPalmaresIds.includes(eventKey)) return;

      if (!event?.AdherentEvent) {
        notificationRef.current?.(
          "Données manquantes pour l'ajout au palmarès.",
          "error",
        );
        return;
      }

      const { adherentId, eventId } = getAdherentEventIds(event);

      if (!adherentId || !eventId) {
        notificationRef.current?.(
          "Données manquantes pour l'ajout au palmarès.",
          "error",
        );
        return;
      }

      setAddingPalmaresIds((prev) => [...prev, eventKey]);

      try {
        const payload = buildPalmaresPayload(event);
        const isAlreadyInPalmares = Boolean(palmaresStatus[statusKey]);

        if (isAlreadyInPalmares) {
          let palmaresRecord = palmaresRecords[statusKey];
          let palmaresId = getPalmaresRecordId(palmaresRecord);

          if (!palmaresId) {
            const checkResponse = await checkPalmaresExists(adherentId, eventId);
            palmaresRecord = extractPalmaresRecord(checkResponse);
            palmaresId = getPalmaresRecordId(palmaresRecord);
          }

          if (!palmaresId) {
            notificationRef.current?.(
              "Impossible de retrouver le palmarès à mettre à jour.",
              "error",
            );
            return;
          }

          const updatedPalmares = await updatePalmares(palmaresId, payload);

          setPalmaresRecords((prev) => ({
            ...prev,
            [statusKey]: extractPalmaresRecord(updatedPalmares) || {
              ...palmaresRecord,
              ...payload,
              id: palmaresId,
            },
          }));

          notificationRef.current?.(
            "Palmarès mis à jour avec succès !",
            "success",
          );

          return;
        }

        const response = await addPalmares(payload);
        const createdRecord = extractPalmaresRecord(response);

        setPalmaresStatus((prev) => ({
          ...prev,
          [statusKey]: true,
        }));

        setPalmaresRecords((prev) => ({
          ...prev,
          [statusKey]: createdRecord,
        }));

        notificationRef.current?.("Ajouté au palmarès avec succès !", "success");
      } catch (error) {
        console.error("Erreur lors de l'ajout au palmarès :", error);

        notificationRef.current?.(
          "Erreur lors de l'ajout ou de la mise à jour du palmarès.",
          "error",
        );
      } finally {
        setAddingPalmaresIds((prev) => prev.filter((id) => id !== eventKey));
      }
    },
    [
      addingPalmaresIds,
      buildPalmaresPayload,
      palmaresRecords,
      palmaresStatus,
    ],
  );

  const handleImageClick = useCallback((url) => {
    if (!url) return;
    setSelectedImage(url);
  }, []);

  const handleOpenLink = useCallback((url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const handleUnregister = useCallback(
    async (event) => {
      const eventKey = getResultEventKey(event);

      if (deletingIds.includes(eventKey)) return;

      const { adherentId, eventId } = getAdherentEventIds(event);

      if (!adherentId || !eventId) {
        notificationRef.current?.(
          "Les données nécessaires pour la suppression sont manquantes.",
          "error",
        );
        return;
      }

      setDeletingIds((prev) => [...prev, eventKey]);

      try {
        await unregisterAdherent(adherentId, eventId);

        setEvents((prevEvents) =>
          prevEvents.filter((currentEvent) => {
            const ids = getAdherentEventIds(currentEvent);

            return !(
              Number(ids.adherentId) === Number(adherentId) &&
              Number(ids.eventId) === Number(eventId)
            );
          }),
        );

        const statusKey = getPalmaresStatusKey(event);

        setPalmaresStatus((prev) => {
          const next = { ...prev };
          delete next[statusKey];
          return next;
        });

        setPalmaresRecords((prev) => {
          const next = { ...prev };
          delete next[statusKey];
          return next;
        });

        notificationRef.current?.("Résultat supprimé avec succès.", "success");
      } catch (error) {
        console.error("Erreur lors de la suppression du résultat :", error);

        notificationRef.current?.(
          "Erreur lors de la suppression du résultat.",
          "error",
        );
      } finally {
        setDeletingIds((prev) => prev.filter((id) => id !== eventKey));
      }
    },
    [deletingIds],
  );

  const handleUnregisterWithConfirm = useCallback(
    async (event) => {
      const confirmDelete = window.confirm(
        "Attention à la suppression de ce résultat :\n\n" +
          "Si vous appartenez à un groupe, cela supprimera le résultat également pour votre coach.\n\n" +
          "Êtes-vous sûr de vouloir continuer ?",
      );

      if (!confirmDelete) return;

      await handleUnregister(event);
    },
    [handleUnregister],
  );

  return {
    adherents,
    selectedAdherent,
    selectedAdherentId,

    events,
    filteredEvents,
    filters,
    form,
    selectedEvent,
    selectedImage,
    isEditModalOpen,

    loading,
    adherentsLoading,
    refreshing,
    saving,

    deletingIds,
    addingPalmaresIds,
    palmaresStatus,
    activeFiltersCount,
    stats,

    fetchData,
    fetchAdherents,
    selectAdherent,

    setSelectedImage,
    handleFilterChange,
    resetFilters,
    handleInputChange,
    handleEdit,
    handleCloseEditModal,
    handleSubmit,
    handleAddToPalmares,
    handleOpenLink,
    handleImageClick,
    handleUnregisterWithConfirm,
  };
}